import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiClient } from "./api/client";
import { dropLocksOf } from "./api/locks";
import { ApiProblem } from "./api/problem";
import type { LocalSession } from "./auth/localAuth";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LocalSessionBanner, LocalSignIn, useLocalAuthStatus } from "./components/LocalSignIn";
import { Notice } from "./components/ProblemAlert";
import { FireContext, useFireContext, type Focus } from "./context";
import type { BasemapConfig } from "./map/basemap";
import { fireModule, pathForRoute, visibleRoutes, type FireRouteId } from "./module";
import { AccessScreen } from "./screens/AccessScreen";
import { ApprovalsScreen } from "./screens/ApprovalsScreen";
import { BoardScreen } from "./screens/BoardScreen";
import { CertificatesScreen } from "./screens/CertificatesScreen";
import { HydrantsScreen } from "./screens/HydrantsScreen";
import { IncidentsScreen } from "./screens/IncidentsScreen";
import { InspectionsScreen } from "./screens/InspectionsScreen";
import { IntakeScreen } from "./screens/IntakeScreen";
import { PremisesScreen } from "./screens/PremisesScreen";
import { ReportsScreen } from "./screens/ReportsScreen";
import { ResourcesScreen } from "./screens/ResourcesScreen";
import type { AccessTokenProvider, Principal } from "./types";
import { AppShell, Avatar, Button, Dot, EmptyState, GlobalSearch, IconFire, SideNav, TopBar, type CommandItem, type NavItem } from "@lcc/ui";
import "./styles.css";

export interface FireAppProps {
  /** Council API base URL from the council directory (never hard-coded). */
  apiBaseUrl: string;
  /** Returns a current access token of the signed-in person (realm `lcc`). */
  getAccessToken: AccessTokenProvider;
  /** Who is signed in normally; null when nobody is (the console then offers sign-in, and the local sign-in if needed). */
  principal: Principal | null;
  /** Start a normal sign-in (the console's Keycloak redirect; the Hub shell's sign-in). */
  onSignIn?: () => void;
  /** Controlled route (the shell maps its router to this). Uncontrolled when omitted. */
  route?: FireRouteId;
  onNavigate?: (route: FireRouteId, path: string) => void;
  /** Where the module is mounted; used for link hrefs. Defaults to fireModule.basePath. */
  basePath?: string;
  /** Basemap of the maps: council tiles (style URL) and/or the console's offline PMTiles; a grid otherwise. */
  basemap?: BasemapConfig;
  /** Test and dev-harness hook; production uses the global fetch. */
  fetchImpl?: typeof fetch;
  /** Share the shell's QueryClient if desired; by default the module owns one (isolation). */
  queryClient?: QueryClient;
  /** Clock for the live clocks, Lusaka days and date ranges (tests pin it). */
  now?: () => Date;
  /** Poll interval of the queue, the board and the live map in ms (default 10 s; 0 turns it off). */
  refreshMs?: number;
  /** Tick of the live clocks in ms (default 1 s; 0 turns it off). */
  clockTickMs?: number;
  /**
   * `standalone` (default: the control-room console and the dev harness) draws the module's own sidebar and top bar;
   * `embedded` (the Hub shell, whose sidebar lists this module's routes from module.ts) draws only the current screen.
   */
  chrome?: FireChrome;
}

const SCREENS: Record<FireRouteId, () => JSX.Element> = {
  intake: IntakeScreen,
  board: BoardScreen,
  incidents: IncidentsScreen,
  resources: ResourcesScreen,
  premises: PremisesScreen,
  certificates: CertificatesScreen,
  inspections: InspectionsScreen,
  hydrants: HydrantsScreen,
  approvals: ApprovalsScreen,
  reports: ReportsScreen,
  access: AccessScreen,
};

/** How the module draws its frame: its own sidebar and top bar (standalone console, dev harness), or none (Hub shell). */
export type FireChrome = "standalone" | "embedded";

const BRAND = { title: fireModule.title, subtitle: "Control room", mark: <IconFire size={20} /> };

/** The console's own frame (standalone): the Hub look with this module's screens in the sidebar and Ctrl K to jump. */
function StandaloneFrame({ children, title, nav, onGo }: { children: ReactNode; title: string; nav?: { items: NavItem[]; currentId: string }; onGo: (id: FireRouteId) => void }) {
  const { principal } = useFireContext();
  const items: CommandItem[] = (nav?.items ?? []).map((i) => ({ id: i.id, label: i.label, href: i.href, group: "Go to", description: fireModule.title }));
  return (
    <AppShell
      mainLabel={title}
      locationKey={nav?.currentId}
      sidebar={
        <SideNav
          brand={BRAND}
          label="Fire & Emergency sections"
          groups={[{ id: "fer", items: nav?.items ?? [] }]}
          currentId={nav?.currentId}
          onNavigate={(_href, item) => onGo(item.id as FireRouteId)}
          footer={
            <>
              <Avatar name={principal.displayName ?? "Staff"} decorative />
              <span className="lcc-nav__footer-text">
                <span className="lcc-nav__footer-name">{principal.displayName ?? "Signed in"}</span>
                <span className="lcc-nav__footer-role">{principal.auth === "local_auth" ? "FER local sign-in" : "Fire & Emergency"}</span>
              </span>
            </>
          }
        />
      }
      topBar={
        <TopBar
          search={<GlobalSearch placeholder="Go to a screen" items={items} onSelect={(i) => onGo(i.id as FireRouteId)} hint="Screens of the Fire & Emergency console." />}
        />
      }
    >
      {children}
    </AppShell>
  );
}

function Shell({ route, navigate, basePath, banner, chrome }: { route: FireRouteId; navigate: (r: FireRouteId) => void; basePath: string; banner: JSX.Element | null; chrome: FireChrome }) {
  const { principal } = useFireContext();
  const visible = visibleRoutes(principal);
  const current = visible.find((r) => r.id === route) ?? visible[0];

  if (!current) {
    const body = (
      <div className="fer-screen">
        {banner}
        <EmptyState
          title="No Fire & Emergency access"
          body={
            <span role="alert">
              You do not have a Fire &amp; Emergency staff role. Crews and fire officers use the Smart City Work app; ask your
              administrator for access if you work for the council.
            </span>
          }
        />
      </div>
    );
    return chrome === "embedded" ? <div className="lcc-root fer-app">{body}</div> : <StandaloneFrame title={fireModule.title} onGo={navigate}>{body}</StandaloneFrame>;
  }
  const Screen = SCREENS[current.id];
  const content = (
    <div className="lcc-root fer-app" data-screen={current.id}>
      {banner && <div className="fer-banners">{banner}</div>}
      <section className="fer-main" aria-label={current.title}>
        {/* One boundary per screen: bad data on one screen never blanks the whole console. */}
        <ErrorBoundary key={current.id} label={current.title} resetKey={`${principal.partyId}|${principal.auth ?? "realm"}`}>
          <Screen />
        </ErrorBoundary>
      </section>
    </div>
  );
  if (chrome === "embedded") return content;
  const items: NavItem[] = visible.map((r) => ({ id: r.id, label: r.title, href: pathForRoute(r.id, basePath) }));
  return (
    <StandaloneFrame title={current.title} nav={{ items, currentId: current.id }} onGo={navigate}>
      {content}
    </StandaloneFrame>
  );
}

/**
 * Don't hammer a backend that answered with a definite problem (403, 404, 422...), nor retry a list that is too long:
 * the answer would be the same. Network errors and 5xx are retried twice.
 */
export function shouldRetry(count: number, err: unknown): boolean {
  return !(err instanceof ApiProblem && ((err.status >= 400 && err.status < 500) || err.code === "list_truncated")) && count < 2;
}

function defaultQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 15_000, refetchOnWindowFocus: false, retry: shouldRetry },
    },
  });
}

const systemNow = () => new Date();

/** The session, the local sign-in and the screens (inside the QueryClientProvider). */
function Session(props: FireAppProps & { basePath: string; now: () => Date; refreshMs: number; clockTickMs: number }) {
  const { apiBaseUrl, fetchImpl, now, refreshMs, clockTickMs, onSignIn } = props;
  const qc = useQueryClient();
  const [local, setLocal] = useState<LocalSession | null>(null);
  const tokenRef = useRef(props.getAccessToken);
  tokenRef.current = props.getAccessToken;
  const localRef = useRef<LocalSession | null>(null);
  localRef.current = local;
  const api = useMemo(
    () => new ApiClient({ apiBaseUrl, getAccessToken: () => localRef.current?.token ?? tokenRef.current(), fetchImpl }),
    [apiBaseUrl, fetchImpl],
  );
  const status = useLocalAuthStatus(api, refreshMs);
  const [localRoute, setLocalRoute] = useState<FireRouteId>("intake");
  const [focus, setFocus] = useState<Focus>({});
  const [offerLocal, setOfferLocal] = useState(false);
  const route = props.route ?? localRoute;
  const onNavigate = props.onNavigate;
  const controlled = props.route !== undefined;
  const principal = local?.principal ?? props.principal;
  const basemap = props.basemap;
  const chrome = props.chrome ?? "standalone";

  const start = (s: LocalSession) => {
    setOfferLocal(false);
    setLocal(s);
  };
  // Ending a local session, by the button or at its expiry (review FW7): nothing it read or held stays for the next.
  const end = useCallback(() => {
    setLocal(null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] === "fer" && q.queryKey[2] === "local_auth" });
    dropLocksOf(qc, "local_auth");
  }, [qc]);

  // A local session ends at its expiry (at most `local_session_hours`); the person then signs in again.
  useEffect(() => {
    if (!local) return;
    const left = Date.parse(local.expiresAt) - now().getTime();
    const t = setTimeout(end, Math.max(0, Math.min(left, 2 ** 31 - 1)));
    return () => clearTimeout(t);
  }, [local, now, end]);

  const ctx = useMemo(() => {
    if (!principal) return null;
    const go = (r: FireRouteId, f: Focus = {}) => {
      setFocus(f);
      if (!controlled) setLocalRoute(r);
      onNavigate?.(r, pathForRoute(r, props.basePath));
    };
    return { api, principal, basemap: basemap ?? {}, now, go, focus, refreshMs, clockTickMs };
  }, [api, principal, basemap, now, focus, controlled, onNavigate, props.basePath, refreshMs, clockTickMs]);

  if (!ctx) {
    return (
      <div className={`lcc-root fer-app fer-signin-page${chrome === "embedded" ? "" : " fer-signin-page--standalone"}`}>
        <div className="fer-signin">
          {chrome !== "embedded" && (
            <div className="lcc-brand">
              <span className="lcc-brand__mark">{BRAND.mark}</span>
              <span className="lcc-brand__text">
                <span className="lcc-brand__title">{BRAND.title}</span>
                <span className="lcc-brand__subtitle">{BRAND.subtitle}</span>
              </span>
            </div>
          )}
          <h1 className="fer-signin__title">Sign in</h1>
          <p className="fer-muted">Sign in to use the Fire &amp; Emergency console.</p>
          {onSignIn && (
            <div className="lcc-actions">
              <Button size="lg" onClick={onSignIn}>
                Sign in
              </Button>
            </div>
          )}
          <ProblemOrLocal api={api} apiBaseUrl={apiBaseUrl} status={status} onSession={start} />
        </div>
      </div>
    );
  }

  const available = status.data?.local_sign_in_available === true;
  const banner = local ? (
    <LocalSessionBanner session={local} status={status.data} onEnd={end} />
  ) : available ? (
    <div className="lcc-notice fer-notice fer-banner" role="status" aria-label="Identity service">
      <Dot tone="warning" />
      <div className="lcc-notice__body">
        <p>
          The identity service is unreachable. Your session continues; if it ends, use the FER local sign-in with your PIN.
        </p>
        {!offerLocal && (
          <div className="fer-actions fer-actions--start">
            <Button size="sm" onClick={() => setOfferLocal(true)}>
              Sign in to FER alone
            </Button>
          </div>
        )}
        {offerLocal && <LocalSignIn api={api} apiBaseUrl={apiBaseUrl} status={status.data} onSession={start} />}
      </div>
    </div>
  ) : null;

  return (
    <FireContext.Provider value={ctx}>
      <Shell route={route} navigate={(r) => ctx.go(r)} basePath={props.basePath} banner={banner} chrome={chrome} />
    </FireContext.Provider>
  );
}

function ProblemOrLocal({ api, apiBaseUrl, status, onSession }: { api: ApiClient; apiBaseUrl: string; status: ReturnType<typeof useLocalAuthStatus>; onSession: (s: LocalSession) => void }) {
  if (status.isLoading) return <p className="fer-muted">Checking whether the identity service answers…</p>;
  if (status.error) return <Notice tone="warning">FER cannot be reached to check the sign-in services. Check the connection.</Notice>;
  return <LocalSignIn api={api} apiBaseUrl={apiBaseUrl} status={status.data} onSession={onSession} />;
}

/** Root of the Fire & Emergency Response admin micro-frontend and the standalone control-room console. */
export function FireApp(props: FireAppProps) {
  const basePath = props.basePath ?? fireModule.basePath;
  const now = props.now ?? systemNow;
  const refreshMs = props.refreshMs ?? 10_000;
  const clockTickMs = props.clockTickMs ?? 1_000;
  const [ownClient] = useState(defaultQueryClient);
  const queryClient = props.queryClient ?? ownClient;
  return (
    <QueryClientProvider client={queryClient}>
      <Session {...props} basePath={basePath} now={now} refreshMs={refreshMs} clockTickMs={clockTickMs} />
    </QueryClientProvider>
  );
}
